import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle2, FileSpreadsheet, Loader2, ScrollText, Share2, SkipForward, Truck, Warehouse } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/date'
import { money } from '@/lib/money'
import { downloadDesignerExcel } from '@/lib/designerExcel'
import { workedBy } from '@/lib/stageLog'
import type { DepartmentDeadline, Order } from '@/types'
import Button from '@/components/ui/Button'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import { StateBadge } from '@/components/ui/Badge'
import LogoLoader from '@/components/ui/LogoLoader'
import StageStrip from '@/components/order/ProductionTimeline'
import { stageRowsFor } from '@/lib/stageRows'
import { useToast } from '@/components/ui/toast'
import { useShippingInfo } from '@/lib/addresses'
import { SELF_PICKUP } from '@/lib/pickupHours'
import PickupDetails from '@/components/order/PickupDetails'
import UrgentBadge from '@/components/job/UrgentBadge'
import OrderItemsCard from '@/components/order/OrderItemsCard'
import BuyAgainButton from '@/components/order/BuyAgainButton'
import ShipmentStatusBadge from '@/components/order/ShipmentStatusBadge'
import ShipmentTimeline from '@/components/order/ShipmentTimeline'
import { shipmentRefetchInterval } from '@/lib/shipment'
import PaymentBadge, { paymentMethodLabels } from '@/components/order/PaymentBadge'

export default function OrderDetail() {
  const { id } = useParams()
  const toast = useToast()
  // Where a self pickup order is collected - the current pickup point,
  // since orders do not snapshot it.
  const shippingInfo = useShippingInfo()

  const { data, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: async () =>
      (await api.get<{ data: Order; department_deadlines: DepartmentDeadline[] }>(`/orders/${id}`)).data,
    refetchInterval: (query) => shipmentRefetchInterval(query.state.data?.data.shipment),
    refetchIntervalInBackground: false,
  })

  const order = data?.data
  const deadlines = data?.department_deadlines ?? []

  if (isLoading || !order) return <LogoLoader />

  const logs = order.job?.stage_logs ?? []

  const copyTrackingLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/track/${order.job!.share_token}`)
    toast.success('Tracking link copied', 'Anyone with this link can view production progress - no sign-in needed.')
  }

  return (
    <div>
      <Link to="/account/orders" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline">
        <ArrowLeft className="size-4" /> Back to orders
      </Link>
      <PageHeader
        title={order.number}
        subtitle={`Job ${order.job?.code ?? '-'}`}
        actions={
          <>
            {/* First, so it leads the row rather than trailing it when the
                actions wrap below sm. */}
            <BuyAgainButton order={order} />
            {order.job?.share_token && (
              <Button variant="outline" onClick={copyTrackingLink}>
                <Share2 className="size-4" /> Copy tracking link
              </Button>
            )}
            {order.job && order.items?.some((item) => (item.personalizations?.length ?? 0) > 0) && (
              <Button variant="outline" onClick={() => downloadDesignerExcel(order.job!, { ...order, agent: order.agent })}>
                <FileSpreadsheet className="size-4" /> Name list Excel
              </Button>
            )}
          </>
        }
      />

      {order.status === 'pending_payment' && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/60 bg-warning/20 p-4 text-sm text-warning">
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="size-4" /> Awaiting payment - this order is not in production yet.
          </p>
          {order.payment_checkout_url && (
            <a
              href={order.payment_checkout_url}
              className="rounded-lg border border-warning/60 px-3 py-1.5 text-xs font-bold hover:bg-warning/10"
            >
              Complete payment
            </a>
          )}
        </div>
      )}
      {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-warning/60 bg-warning/20 p-4 text-sm font-semibold text-warning">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          Payment due: {money(order.total)}. This order is being produced on credit - please settle it with MPG Printing.
        </div>
      )}
      {order.status === 'cancelled' && (
        <div className="mb-4 rounded-lg border border-foreground/30 bg-foreground/10 p-4 text-sm text-foreground/80">
          {order.cancelled_by_admin ? (
            <>
              This order was cancelled{order.cancel_reason ? `: ${order.cancel_reason.replace(/\.+$/, '')}` : ''}.
              {order.payment_status === 'refunded' && ` ${money(order.refunded_amount)} has been refunded.`}
              {order.payment_status === 'refund_due' &&
                ` Your payment of ${money(order.total)} will be refunded, and we will let you know once it is done.`}
            </>
          ) : (
            <>
              Payment was not completed in time, so this order was cancelled and nothing was charged. Place a new
              order whenever you are ready.
            </>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <OrderItemsCard order={order} variant="customer" />

          <Card>
            <SectionLabel>Production timeline</SectionLabel>
            {order.job?.warehouse && (
              <p className="mb-3 flex items-center gap-2 rounded-lg border border-primary/60 bg-primary/10 p-2.5 text-xs font-medium text-primary">
                <Warehouse className="size-4" /> This job is produced at external warehouse partner: {order.job.warehouse.name}
              </p>
            )}
            {logs.length === 0 && (
              <p className="mb-4 rounded-lg bg-app p-2.5 text-xs text-muted">
                Queued for production - the floor team will scan the job QR to begin.
              </p>
            )}
            <ol className="space-y-3">
              {logs.map((log) => (
                <li key={log.id} className="flex items-start gap-3 text-sm">
                  {log.action === 'complete' ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-status-success" />
                  ) : log.action === 'skip' ? (
                    <SkipForward className="mt-0.5 size-4 shrink-0 text-muted" />
                  ) : log.action === 'issue' ? (
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-status-danger" />
                  ) : (
                    <Loader2 className="mt-0.5 size-4 shrink-0 text-status-processing" />
                  )}
                  <div>
                    <p className="font-semibold">
                      {log.department} - {log.action === 'start' ? 'started' : log.action === 'complete' ? 'completed' : log.action === 'skip' ? 'skipped' : 'issue flagged'}
                    </p>
                    <p className="text-xs text-muted">
                      {formatDateTime(log.at)} by {workedBy(log)}
                    </p>
                    {log.reason && <p className="mt-0.5 text-xs font-semibold text-status-danger">{log.reason}</p>}
                  </div>
                </li>
              ))}
            </ol>

            {deadlines.length > 0 && (
              <div className="mt-5 border-t border-foreground/10 pt-4">
                <StageStrip
                  rows={stageRowsFor(deadlines, order)}
                  etd={order.expected_completion_date}
                />
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          {order.payment_status && (
            <Card>
              <SectionLabel>Payment</SectionLabel>
              <div className="space-y-2 text-sm">
                <p className="flex items-center justify-between gap-2">
                  <span className="text-muted">Status</span>
                  <PaymentBadge status={order.payment_status} />
                </p>
                {order.payment_method && (
                  <p className="flex justify-between">
                    <span className="text-muted">Method</span>
                    <span className="font-semibold">{paymentMethodLabels[order.payment_method]}</span>
                  </p>
                )}
                {order.paid_at && (
                  <p className="flex justify-between">
                    <span className="text-muted">Paid</span>
                    <span className="font-semibold">{formatDateTime(order.paid_at)}</span>
                  </p>
                )}
              </div>
              {/* Not while a gateway payment is still in flight. */}
              {order.status !== 'pending_payment' && (
                <Link to={`/account/orders/${order.id}/invoice`} target="_blank" rel="noreferrer" className="mt-4 block">
                  <Button variant="outline" className="w-full">
                    <ScrollText className="size-4" /> View invoice
                  </Button>
                </Link>
              )}
            </Card>
          )}

          <Card>
            <SectionLabel>Schedule</SectionLabel>
            <div className="space-y-2 text-sm">
              <p className="flex items-center justify-between gap-2">
                <span className="text-muted">Status</span>
                <StateBadge state={order.status} />
              </p>
              <p className="flex justify-between">
                <span className="text-muted">Ordered</span>
                <span className="font-semibold">{formatDate(order.created_at)}</span>
              </p>
              <p className="flex justify-between">
                <span className="text-muted">Production date</span>
                <span className="font-semibold">{formatDate(order.production_date)}</span>
              </p>
              <p className="flex justify-between">
                <span className="text-muted">Delivery due</span>
                <span className="inline-flex items-center gap-2 font-semibold text-brand-600">
                  <UrgentBadge urgent={order.is_urgent} variant="storefront" />
                  {formatDate(order.expected_completion_date)}
                </span>
              </p>
            </div>
          </Card>

          {(order.delivery_method || order.delivery_address || order.shipment || order.tracking_number || order.notes) && (
            <Card>
              <SectionLabel>Delivery</SectionLabel>
              <div className="space-y-2 text-sm">
                {order.delivery_method && (
                  <p className="flex items-center justify-between">
                    <span className="text-muted">Method</span>
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <Truck className="size-3.5 text-primary" /> {order.delivery_method}
                    </span>
                  </p>
                )}
                {order.delivery_method === SELF_PICKUP && shippingInfo.data && (
                  <PickupDetails pickup={shippingInfo.data.pickup} />
                )}
                {order.delivery_address && (
                  <div className="rounded-lg bg-app p-2 text-xs">
                    <p className="font-bold">Deliver to</p>
                    <p className="mt-0.5 font-semibold">
                      {order.delivery_address.recipient_name} - {order.delivery_address.phone}
                    </p>
                    <p className="text-muted">
                      {order.delivery_address.line1}
                      {order.delivery_address.line2 ? `, ${order.delivery_address.line2}` : ''},{' '}
                      {order.delivery_address.postcode} {order.delivery_address.city}, {order.delivery_address.state}
                    </p>
                  </div>
                )}
                {order.shipment && (
                  <>
                    <p className="flex items-center justify-between gap-2">
                      <span className="text-muted">Courier</span>
                      <span className="font-semibold">{order.shipment.carrier}</span>
                    </p>
                    <p className="flex items-center justify-between gap-2">
                      <span className="text-muted">Courier status</span>
                      <ShipmentStatusBadge status={order.shipment.status} variant="storefront" />
                    </p>
                    {order.shipment.delivered_at && (
                      <p className="flex justify-between">
                        <span className="text-muted">Delivered</span>
                        <span className="font-semibold text-success">{formatDateTime(order.shipment.delivered_at)}</span>
                      </p>
                    )}
                  </>
                )}
                {order.tracking_number && (
                  <p className="flex items-center justify-between gap-2">
                    <span className="shrink-0 text-muted">Tracking no.</span>
                    <span className="min-w-0 break-all font-mono text-xs font-semibold">{order.tracking_number}</span>
                  </p>
                )}
                {order.shipment && (
                  <div className="space-y-2 border-t border-line pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted">Delivery updates</p>
                    <ShipmentTimeline events={order.shipment.events ?? []} status={order.shipment.status} variant="storefront" />
                  </div>
                )}
                {order.tracking_url && (
                  <a href={order.tracking_url} target="_blank" rel="noreferrer" className="block">
                    <Button variant="outline" className="w-full">
                      <Truck className="size-4" /> Track parcel
                    </Button>
                  </a>
                )}
                {order.notes && (
                  <p className="rounded-lg bg-app p-2 text-xs">
                    <span className="font-bold">Notes:</span> {order.notes}
                  </p>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
